import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, signal } from '@angular/core';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export interface SparklineData {
	val: number;
	peaking: boolean;
	unit: string;
}

export interface SparklineCellParams extends ICellRendererParams {
	values?: SparklineData[];
	yMin?: number;
	yMax?: number;
	paddingX?: number;
	paddingY?: number;
	colorNormal?: string;
	colorPeak?: string;
	strokeWidth?: number;
	width?: number;
	height?: number;
}

type Point = { x: number; y: number };
type Segment = {
	dLine: string;
	stroke: string;
};

@Component({
	selector: 'sh-sparkline-renderer',
	standalone: true,
	imports: [CommonModule, AgGridModule],
	templateUrl: './sparkline-renderer.component.html',
	styleUrl: './sparkline-renderer.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SparklineRendererComponent implements ICellRendererAngularComp {
	private values = signal<SparklineData[]>([]);
	private yMin = signal(-100);
	private yMax = signal(0);
	private paddingX = signal(5);
	private paddingY = signal(5);
	private colorNormal = signal('rgb(25, 118, 210)');
	private colorPeak = signal('rgb(211, 47, 47)');

	protected hoverIndex = signal<number | null>(null);
	protected strokeWidth = signal(1.5);
	protected width = signal(100);
	protected height = signal(30);
	private baselineY = signal(0);

	private points = signal<Point[]>([]);
	protected segments = signal<Segment[]>([]);

	constructor() {
		effect(
			() => {
				this.rebuild();
			},
			{ allowSignalWrites: true }
		);
	}

	public agInit(params: SparklineCellParams): void {
		this.values.set(params?.value ?? []);
		this.yMin.set(params.yMin ?? -100);
		this.yMax.set(params.yMax ?? 0);
		this.paddingX.set(params.paddingX ?? 5);
		this.paddingY.set(params.paddingY ?? 5);
		this.colorNormal.set(params.colorNormal ?? 'rgb(25, 118, 210)');
		this.colorPeak.set(params.colorPeak ?? 'rgb(211, 47, 47)');
		this.strokeWidth.set(params.strokeWidth ?? 1.5);
		this.width.set(params.width ?? 100);
		this.height.set(params.height ?? 30);
		this.baselineY.set(0);
		this.hoverIndex.set(null);
	}

	public refresh(params: SparklineCellParams): boolean {
		this.values.set(params?.value ?? []);
		return true;
	}

	public onMouseMove(evt: MouseEvent): void {
		if (!this.points().length) return;

		const target = <SVGSVGElement>evt.currentTarget;
		const rect = target.getBoundingClientRect();
		const mouseX = evt.clientX - rect.left;
		const viewX = (mouseX / rect.width) * this.width();

		let idx = 0;
		let best = Number.POSITIVE_INFINITY;
		for (let i = 0; i < this.points().length; i++) {
			const dx = Math.abs(this.points()[i].x - viewX);
			if (dx < best) {
				best = dx;
				idx = i;
			}
		}
		this.hoverIndex.set(idx);
	}

	public onMouseLeave(): void {
		this.hoverIndex.set(null);
	}

	private rebuild(): void {
		this.baselineY.set(this.height() - this.paddingY());
		this.points.set(
			this.values().map((v, i) => ({
				x: this.toX(i, this.values().length),
				y: this.toY(v.val)
			}))
		);
		this.segments.set(this.makeSegments());
	}

	private toX(index: number, totalPoints: number): number {
		const availableXSpace = this.width() - this.paddingX() * 2;
		return this.paddingX() + (totalPoints <= 1 ? 0 : (index / (totalPoints - 1)) * availableXSpace);
	}

	private toY(value: number): number {
		const availableYSpace = this.height() - this.paddingY() * 2;
		const normal = (value - this.yMin()) / (this.yMax() - this.yMin());
		return this.height() - this.paddingY() - normal * availableYSpace;
	}

	private makeSegments(): Segment[] {
		const segs: Segment[] = [];
		if (this.points().length < 2) return segs;

		for (let i = 0; i < this.points().length - 1; i++) {
			const p1 = this.points()[i];
			const p2 = this.points()[i + 1];
			const isPeaking = this.values()[i].peaking || this.values()[i + 1].peaking;
			const stroke = isPeaking ? this.colorPeak() : this.colorNormal();
			const dLine = `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`;
			segs.push({ dLine, stroke });
		}
		return segs;
	}

	public get hoverX(): number | null {
		return this.hoverIndex() === null ? null : this.points()[this.hoverIndex() ?? 0].x;
	}
	public get hoverY(): number | null {
		return this.hoverIndex() === null ? null : this.points()[this.hoverIndex() ?? 0].y;
	}
	public get hoverValue(): string {
		return this.hoverIndex() === null
			? ''
			: `${this.values()[this.hoverIndex() ?? 0].val} ${this.values()[this.hoverIndex() ?? 0].unit}`;
	}
	public get tooltipWidth(): number {
		return this.hoverValue.length * 6;
	}
	public get tooltipTransform(): string {
		if (this.hoverX === null || this.hoverY === null) {
			return '';
		}

		const halfWidth = this.width() / 2;
		const tooltipOffsetX = this.hoverX > halfWidth ? -this.tooltipWidth - 4 : 4;
		const tooltipOffsetY = -8;

		return `translate(${this.hoverX + tooltipOffsetX},${this.hoverY + tooltipOffsetY})`;
	}
}
